"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_ACTION_DATE_ONLY = exports.AppActionFieldValidationError = void 0;
exports.isAppActionPointInTimeText = isAppActionPointInTimeText;
exports.parseAppActionPointInTime = parseAppActionPointInTime;
exports.parseAppActionPointInTimeWithOffset = parseAppActionPointInTimeWithOffset;
exports.validateAndSanitizeAppActionFields = validateAndSanitizeAppActionFields;
exports.sanitizeAppActionString = sanitizeAppActionString;
exports.sanitizeAppBaseAttachmentFilename = sanitizeAppBaseAttachmentFilename;
exports.isAppBaseAttachmentMimeAllowed = isAppBaseAttachmentMimeAllowed;
const database_identity_1 = require("@streamlain/database-identity");
class AppActionFieldValidationError extends Error {
    constructor(errors) {
        super('Validation failed');
        this.errors = errors;
        this.name = 'AppActionFieldValidationError';
    }
}
exports.AppActionFieldValidationError = AppActionFieldValidationError;
/** An App date without a time: UTC midnight of its calendar day. */
exports.APP_ACTION_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_POINT_IN_TIME = /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2}))?$/;
/**
 * Whether text is a date an App may hand over: an ISO date or an ISO
 * date-time that names its zone. Formats a runtime guesses at (`12.10.2026`
 * reads as 10 December) are refused.
 */
function isAppActionPointInTimeText(text) {
    return exports.APP_ACTION_DATE_ONLY.test(text)
        ? appActionDateParts(text) !== null
        : parseAppActionPointInTimeWithOffset(text) !== null;
}
/**
 * The one reading of a date an App hands over, in milliseconds: an ISO date
 * is UTC midnight of its calendar day; an ISO date-time names its own zone.
 * The field check, the platform's row writer and the local write port of the
 * StreamKit read every accepted text to the same moment.
 */
function parseAppActionPointInTime(text) {
    if (!appActionDateParts(text))
        return null;
    return exports.APP_ACTION_DATE_ONLY.test(text)
        ? (0, database_identity_1.parseDatabaseDateText)(text, database_identity_1.DATABASE_STORED_TEXT_TIME_ZONE)
        : parseAppActionPointInTimeWithOffset(text);
}
/** The moment of an App date-time that names its zone; null for a date. */
function parseAppActionPointInTimeWithOffset(text) {
    if (!appActionDateParts(text) || exports.APP_ACTION_DATE_ONLY.test(text))
        return null;
    const time = Date.parse(text);
    return Number.isNaN(time) ? null : time;
}
function appActionDateParts(text) {
    const match = ISO_POINT_IN_TIME.exec(text);
    if (!match)
        return null;
    const [year, month, day] = [+match[1], +match[2], +match[3]];
    // The runtime rolls a day the month does not have into the next month.
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day)
        return null;
    return match;
}
function validateAndSanitizeAppActionFields(fields, fieldConfigs) {
    const result = {};
    const errors = [];
    for (const config of fieldConfigs) {
        const value = fields[config.columnId];
        if (config.validation?.required &&
            (value === null || value === undefined || value === '')) {
            errors.push(`${config.label} is required`);
            continue;
        }
        if (value === null || value === undefined || value === '') {
            result[config.columnId] = null;
            continue;
        }
        if (config.validation?.maxLength !== undefined &&
            sanitizeAppActionString(String(value)).length >
                config.validation.maxLength) {
            errors.push(`${config.label} must be at most ${config.validation.maxLength} characters`);
            continue;
        }
        switch (config.type) {
            case 'text':
            case 'textarea': {
                const str = sanitizeAppActionString(String(value));
                if (config.validation?.minLength &&
                    str.length < config.validation.minLength) {
                    errors.push(`${config.label} must be at least ${config.validation.minLength} characters`);
                }
                if (config.validation?.pattern) {
                    const re = new RegExp(config.validation.pattern);
                    if (!re.test(str)) {
                        errors.push(config.validation.patternMessage ??
                            `${config.label} has an invalid format`);
                    }
                }
                result[config.columnId] = str;
                break;
            }
            case 'email': {
                const email = sanitizeAppActionString(String(value));
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    errors.push(`${config.label} must be a valid email address`);
                }
                result[config.columnId] = email;
                break;
            }
            case 'url': {
                const url = sanitizeAppActionString(String(value));
                try {
                    new URL(url);
                }
                catch {
                    errors.push(`${config.label} must be a valid URL`);
                }
                result[config.columnId] = url;
                break;
            }
            case 'number': {
                const num = Number(value);
                if (Number.isNaN(num)) {
                    errors.push(`${config.label} must be a valid number`);
                }
                else {
                    if (config.validation?.min !== undefined &&
                        num < config.validation.min) {
                        errors.push(`${config.label} must be at least ${config.validation.min}`);
                    }
                    if (config.validation?.max !== undefined &&
                        num > config.validation.max) {
                        errors.push(`${config.label} must be at most ${config.validation.max}`);
                    }
                    result[config.columnId] = num;
                }
                break;
            }
            case 'date': {
                const dateStr = String(value);
                if (!isAppActionPointInTimeText(dateStr)) {
                    errors.push(`${config.label} must be a valid date`);
                }
                result[config.columnId] = dateStr;
                break;
            }
            case 'select': {
                const selected = String(value);
                if (config.options && !config.options.includes(selected)) {
                    errors.push(`${config.label} has an invalid selection`);
                }
                result[config.columnId] = selected;
                break;
            }
            case 'multi-select': {
                const selections = Array.isArray(value) ? value.map(String) : [];
                if (config.options) {
                    const invalid = selections.filter(selection => !config.options.includes(selection));
                    if (invalid.length > 0) {
                        errors.push(`${config.label} has invalid selections: ${invalid.join(', ')}`);
                    }
                }
                result[config.columnId] = selections;
                break;
            }
            case 'checkbox': {
                result[config.columnId] = Boolean(value);
                break;
            }
            default:
                result[config.columnId] = sanitizeAppActionString(String(value));
        }
    }
    if (errors.length > 0) {
        throw new AppActionFieldValidationError(errors);
    }
    return result;
}
function sanitizeAppActionString(input) {
    return input
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .trim();
}
/** Cut by code points and drop lone surrogates: the name stays URI-encodable. */
function sanitizeAppBaseAttachmentFilename(filename) {
    return Array.from(filename
        .replace(/[\\/"\u0000\r\n]/g, '_')
        .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '_')
        .trim())
        .slice(0, 180)
        .join('');
}
function isAppBaseAttachmentMimeAllowed(mimeType, acceptedMimeTypes) {
    const normalized = mimeType.toLowerCase();
    return acceptedMimeTypes.some(pattern => {
        const candidate = pattern.toLowerCase();
        return candidate.endsWith('/*')
            ? normalized.startsWith(`${candidate.slice(0, -2)}/`)
            : normalized === candidate;
    });
}
